import { useEffect, useMemo, useState } from "react";
import {
  DEFAULT_AI_ASSISTANT,
  fetchAiAssistant,
  updateAiAssistant,
  type AiAssistant,
  type KnowledgeBlock,
} from "../api/aiAssistant.ts";
import { useToast } from "../components/ToastProvider.tsx";

type BlockRow = { id: string; title: string; content: string };

const SUGGESTIONS = [
  "Sobre a empresa",
  "Produtos e preços",
  "Perguntas frequentes",
  "Formas de pagamento",
  "Garantia e políticas",
  "Área de atendimento",
];

const PLACEHOLDERS: Record<string, string> = {
  "Sobre a empresa": "Somos especializados em manutenção predial e atendemos a região metropolitana...",
  "Produtos e preços": "Instalação elétrica — a partir de R$ 250\nVistoria técnica — R$ 150\nManutenção mensal — sob orçamento",
  "Perguntas frequentes": "Vocês atendem aos finais de semana? Sim, com agendamento.\nQual a forma de pagamento? Pix, cartão e boleto.",
  "Formas de pagamento": "Pix, cartão de crédito em até 12x e boleto.",
  "Garantia e políticas": "Todo serviço tem 90 dias de garantia. Reagendamento sem custo até 24h antes.",
  "Área de atendimento": "Atendemos a capital e a região metropolitana num raio de 40 km.",
};

function genId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return "b" + Math.random().toString(36).slice(2);
}

function toRows(blocks: KnowledgeBlock[]): BlockRow[] {
  return blocks.map((b) => ({ id: genId(), title: b.title, content: b.content }));
}

function signature(companyName: string, tone: string, hours: string, blocks: BlockRow[]): string {
  return JSON.stringify({
    companyName: companyName.trim(),
    tone: tone.trim(),
    hours: hours.trim(),
    blocks: blocks.map((b) => ({ title: b.title.trim(), content: b.content.trim() })),
  });
}

export function Automacao() {
  const { showToast } = useToast();
  const [enabled, setEnabled] = useState(DEFAULT_AI_ASSISTANT.enabled);
  const [companyName, setCompanyName] = useState("");
  const [tone, setTone] = useState("");
  const [hours, setHours] = useState("");
  const [blocks, setBlocks] = useState<BlockRow[]>([]);
  const [savedSig, setSavedSig] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [togglingEnabled, setTogglingEnabled] = useState(false);
  const [error, setError] = useState("");

  function hydrate(data: AiAssistant) {
    setEnabled(data.enabled);
    setCompanyName(data.companyName);
    setTone(data.tone);
    setHours(data.hours);
    const rows = toRows(data.knowledgeBlocks);
    setBlocks(rows);
    setSavedSig(signature(data.companyName, data.tone, data.hours, rows));
  }

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchAiAssistant()
      .then((data) => {
        if (!active) return;
        hydrate(data);
        setError("");
      })
      .catch((err) => {
        if (!active) return;
        setError(err instanceof Error ? err.message : "Falha ao carregar a automação");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const currentSig = useMemo(
    () => signature(companyName, tone, hours, blocks),
    [companyName, tone, hours, blocks],
  );
  const dirty = currentSig !== savedSig;

  function addBlock(title = "") {
    setBlocks((prev) => [...prev, { id: genId(), title, content: "" }]);
  }

  function updateBlock(id: string, patch: Partial<Omit<BlockRow, "id">>) {
    setBlocks((prev) => prev.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  }

  function removeBlock(id: string) {
    setBlocks((prev) => prev.filter((b) => b.id !== id));
  }

  function moveBlock(id: string, dir: -1 | 1) {
    setBlocks((prev) => {
      const idx = prev.findIndex((b) => b.id === id);
      const next = idx + dir;
      if (idx < 0 || next < 0 || next >= prev.length) return prev;
      const copy = [...prev];
      const [item] = copy.splice(idx, 1);
      copy.splice(next, 0, item);
      return copy;
    });
  }

  async function toggleEnabled(value: boolean) {
    const previous = enabled;
    setEnabled(value);
    setTogglingEnabled(true);
    setError("");
    try {
      const result = await updateAiAssistant({ enabled: value });
      setEnabled(result.enabled);
      showToast(value ? "Assistente de IA ativado" : "Assistente de IA pausado", "success");
    } catch (err) {
      setEnabled(previous);
      const message = err instanceof Error ? err.message : "Falha ao salvar";
      setError(message);
      showToast(message, "error");
    } finally {
      setTogglingEnabled(false);
    }
  }

  async function save() {
    setSaving(true);
    setError("");
    try {
      const knowledgeBlocks: KnowledgeBlock[] = blocks
        .map((b) => ({ title: b.title.trim(), content: b.content.trim() }))
        .filter((b) => b.title || b.content);
      const result = await updateAiAssistant({ companyName, tone, hours, knowledgeBlocks });
      // Re-hidrata mantendo enabled atual (toggle é salvo à parte).
      hydrate({ ...result, enabled });
      showToast("Base de conhecimento salva", "success");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Falha ao salvar";
      setError(message);
      showToast(message, "error");
    } finally {
      setSaving(false);
    }
  }

  const usedTitles = new Set(blocks.map((b) => b.title.trim().toLowerCase()));
  const availableSuggestions = SUGGESTIONS.filter((s) => !usedTitles.has(s.toLowerCase()));

  return (
    <section className="automacao-shell" aria-label="Automação">
      <header className="automacao-head">
        <div className="automacao-head-copy">
          <span className="automacao-eyebrow">Inteligência artificial</span>
          <h1>Assistente pessoal</h1>
          <p>
            Monte a base de conhecimento da sua empresa em blocos. A IA usa tudo isso para responder
            automaticamente os contatos que chegam pelo WhatsApp e Instagram — até um humano assumir.
          </p>
        </div>
      </header>

      <article className={`automacao-power ${enabled ? "is-on" : ""}`}>
        <div className="automacao-power-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <rect x="4.5" y="8" width="15" height="11" rx="3" />
            <path d="M12 4.6V8M9.3 13h.01M14.7 13h.01M3.5 12v3M20.5 12v3" />
            <circle cx="12" cy="4" r="1.1" />
          </svg>
        </div>
        <div className="automacao-power-copy">
          <h2>{enabled ? "Assistente ativo" : "Assistente pausado"}</h2>
          <p>
            {enabled
              ? "A IA está respondendo novos contatos com a base abaixo."
              : "Ative para a IA começar a responder novos contatos automaticamente."}
          </p>
        </div>
        <label className={`automacao-toggle ${enabled ? "is-on" : ""}`}>
          <input
            type="checkbox"
            checked={enabled}
            disabled={loading || togglingEnabled}
            onChange={(event) => void toggleEnabled(event.currentTarget.checked)}
          />
          <span className="automacao-toggle-track" aria-hidden="true">
            <span className="automacao-toggle-thumb" />
          </span>
          <span className="automacao-toggle-label">{enabled ? "Ativado" : "Desativado"}</span>
        </label>
      </article>

      <div className="automacao-behavior">
        <div className="automacao-behavior-item">
          <BehaviorIcon kind="human" />
          <div>
            <strong>Responde até um humano assumir</strong>
            <span>Quando você ou um vendedor responder a conversa, a IA para automaticamente.</span>
          </div>
        </div>
        <div className="automacao-behavior-item">
          <BehaviorIcon kind="escalate" />
          <div>
            <strong>Escala para o humano quando não sabe</strong>
            <span>Se a IA não tiver a resposta na base, ela avisa que um atendente vai continuar.</span>
          </div>
        </div>
      </div>

      <form
        className="automacao-form"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <div className="automacao-grid">
          <Field
            label="Nome da empresa"
            hint="Como a IA deve se apresentar"
            value={companyName}
            onChange={setCompanyName}
            placeholder="Ex.: Construmoove Serviços"
            disabled={loading}
          />
          <Field
            label="Tom de voz"
            hint="Como a IA deve falar"
            value={tone}
            onChange={setTone}
            placeholder="Cordial, objetivo e profissional"
            disabled={loading}
          />
          <Field
            label="Horário de atendimento"
            hint="Quando a empresa atende"
            value={hours}
            onChange={setHours}
            placeholder="Seg a sex, 8h às 18h"
            disabled={loading}
          />
        </div>

        <div className="automacao-kb">
          <div className="automacao-kb-head">
            <div>
              <h3>Base de conhecimento</h3>
              <p>Crie quantos blocos quiser com tudo que a IA precisa saber sobre a empresa.</p>
            </div>
            <span className="automacao-kb-count">{blocks.length} {blocks.length === 1 ? "bloco" : "blocos"}</span>
          </div>

          {blocks.length === 0 ? (
            <div className="automacao-kb-empty">
              <div className="automacao-kb-empty-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <rect x="4" y="5" width="16" height="14" rx="2.5" />
                  <path d="M8 9h8M8 12.5h8M8 16h5" />
                </svg>
              </div>
              <strong>Nenhum bloco ainda</strong>
              <span>Comece por uma sugestão ou crie um bloco do zero.</span>
            </div>
          ) : (
            <div className="automacao-blocks">
              {blocks.map((block, index) => (
                <div className="automacao-block" key={block.id}>
                  <div className="automacao-block-top">
                    <input
                      className="automacao-block-title"
                      type="text"
                      value={block.title}
                      placeholder="Título do bloco — ex.: Preços"
                      disabled={loading}
                      onChange={(event) => updateBlock(block.id, { title: event.currentTarget.value })}
                    />
                    <div className="automacao-block-tools">
                      <button
                        type="button"
                        className="automacao-icon-btn"
                        title="Mover para cima"
                        aria-label="Mover para cima"
                        disabled={loading || index === 0}
                        onClick={() => moveBlock(block.id, -1)}
                      >
                        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m7 14 5-5 5 5" /></svg>
                      </button>
                      <button
                        type="button"
                        className="automacao-icon-btn"
                        title="Mover para baixo"
                        aria-label="Mover para baixo"
                        disabled={loading || index === blocks.length - 1}
                        onClick={() => moveBlock(block.id, 1)}
                      >
                        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m7 10 5 5 5-5" /></svg>
                      </button>
                      <button
                        type="button"
                        className="automacao-icon-btn is-danger"
                        title="Remover bloco"
                        aria-label="Remover bloco"
                        disabled={loading}
                        onClick={() => removeBlock(block.id)}
                      >
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M5 7h14M9 7V5h6v2M7 7l1 12h8l1-12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <textarea
                    className="automacao-block-content"
                    value={block.content}
                    rows={4}
                    placeholder={PLACEHOLDERS[block.title.trim()] || "Escreva aqui as informações deste bloco..."}
                    disabled={loading}
                    onChange={(event) => updateBlock(block.id, { content: event.currentTarget.value })}
                  />
                </div>
              ))}
            </div>
          )}

          {availableSuggestions.length > 0 ? (
            <div className="automacao-suggestions">
              <span className="automacao-suggestions-label">Sugestões:</span>
              {availableSuggestions.map((s) => (
                <button
                  key={s}
                  type="button"
                  className="automacao-chip"
                  disabled={loading}
                  onClick={() => addBlock(s)}
                >
                  + {s}
                </button>
              ))}
            </div>
          ) : null}

          <button type="button" className="automacao-add" disabled={loading} onClick={() => addBlock()}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 6v12M6 12h12" /></svg>
            Adicionar bloco
          </button>
        </div>

        {error ? <div className="automacao-alert">{error}</div> : null}

        <div className="automacao-actions">
          <button type="submit" className="automacao-save" disabled={loading || saving || !dirty}>
            {saving ? "Salvando..." : dirty ? "Salvar base de conhecimento" : "Tudo salvo"}
          </button>
        </div>
      </form>
    </section>
  );
}

function Field({
  label,
  hint,
  value,
  onChange,
  placeholder,
  disabled,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  return (
    <label className="automacao-field">
      <span className="automacao-field-label">{label}</span>
      {hint ? <span className="automacao-field-hint">{hint}</span> : null}
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(event) => onChange(event.currentTarget.value)}
      />
    </label>
  );
}

function BehaviorIcon({ kind }: { kind: "human" | "escalate" }) {
  if (kind === "human") {
    return (
      <svg className="automacao-behavior-svg" viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="8" r="3.4" />
        <path d="M5.5 19a6.5 6.5 0 0 1 13 0" />
      </svg>
    );
  }
  return (
    <svg className="automacao-behavior-svg" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 4.5 20 19H4l8-14.5Z" />
      <path d="M12 10v4M12 16.5h.01" />
    </svg>
  );
}
